import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import UserContext from '../Components/UserContext';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import Fetcher from '../lib/Fetcher';
import Logout from '../Components/Logout';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, setSession } = useContext(UserContext);

  // Try to restore session if it's missing (e.g. on page refresh)
  const { data: sessionData, isLoading: isSessionLoading } = useSWR(
    !session ? '/auth/session' : null,
    Fetcher,
    { shouldRetryOnError: false }
  );

  useEffect(() => {
    if (sessionData && !session) {
      setSession(sessionData);
    }
  }, [sessionData, session, setSession]);

  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });
  const [paymentMethod, setPaymentMethod] = useState('UPI');
  const [useSavedAddress, setUseSavedAddress] = useState(true);
  const [savedAddressFromProfile, setSavedAddressFromProfile] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState({ paymentSystemMode: 'manual' });
  const [showSimulatedGateway, setShowSimulatedGateway] = useState(false);
  const [placingOrderData, setPlacingOrderData] = useState(null);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // Fetch profile address and last order address
  useEffect(() => {
    if (session) {
      const user = session.user || session;

      // 1. Set initial basic info from session immediately
      const basicInfo = {
        name: user.name || '',
        phone: user.mobile || user.phone || '',
      };

      setShippingAddress(prev => ({
        ...prev,
        ...basicInfo
      }));

      // 2. Fetch full profile to get saved address
      const fetchProfileAddress = async () => {
        try {
          const response = await HttpInterceptor.get('/api/students/profile');
          if (response.data?.success || response.status === 200) {
            const data = response.data?.data || response.data;
            if (data?.address) {
              // Combine basic info with address data
              const fullSavedAddress = {
                ...basicInfo,
                ...data.address
              };

              // Load profile address for all routes if authenticated
              setSavedAddressFromProfile(fullSavedAddress);

              // Clear selection if saved address is empty/incomplete
              const isUsable = !!(fullSavedAddress.addressLine1 || fullSavedAddress.city);
              if (!isUsable) {
                setUseSavedAddress(false);
              } else if (useSavedAddress) {
                setShippingAddress(prev => ({
                  ...prev,
                  ...fullSavedAddress
                }));
              }
            }
          }
        } catch (err) {
          console.log('Could not fetch profile address');
        }
      };

      fetchProfileAddress();
    }
  }, [session]);

  // Update shipping address when useSavedAddress toggles
  useEffect(() => {
    if (useSavedAddress && savedAddressFromProfile) {
      setShippingAddress(prev => ({
        ...prev,
        ...savedAddressFromProfile
      }));
    } else if (useSavedAddress === false) {
      // Keep current values but allow editing (effectively doing nothing clears nothing, which is good)
      // Optionally could clear fields if that's the desired UX, but usually keeping them is better.
      // Maybe just clear the fields if they match saved address? No, let's keep them.
    }
  }, [useSavedAddress, savedAddressFromProfile]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await HttpInterceptor.get('/api/admin/public/settings');
        setPaymentSettings(response.data.data);
      } catch (error) {
        console.error('Failed to load payment settings');
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const savedCart = localStorage.getItem('marketplaceCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      toast.error('No items in cart');
      navigate('/market');
    }
  }, [navigate]);

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      const discount = (itemTotal * item.discount) / 100;
      return total + itemTotal - discount;
    }, 0);
  };

  const handleSendOtp = async () => {
    if (!mobile || mobile.length < 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    setIsSendingOtp(true);
    try {
      const { data } = await HttpInterceptor.post('/auth/send-otp', { mobile });
      setIsOtpSent(true);
      toast.success('OTP sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    setIsVerifyingOtp(true);
    try {
      const res = await HttpInterceptor.post('/auth/verify-otp-login', { mobile, otp });
      if (res.data) {
        setSession(res.data.user);
        toast.success('Authenticated successfully!');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    if (!shippingAddress.name || !shippingAddress.phone || !shippingAddress.addressLine1 ||
      !shippingAddress.city || !shippingAddress.state || !shippingAddress.pincode) {
      toast.error('Please fill all required shipping address fields');
      return;
    }

    setLoading(true);
    try {
      // Send all items in one request. The backend will split them by reseller if needed.
      const orderPayload = {
        items: cart.map(item => ({
          productId: item.productId,
          title: item.title,
          price: item.price,
          discount: item.discount,
          gstRate: item.gstRate || 18,
          hsnCode: item.hsnCode || '',
          resellerState: item.resellerState || 'platform',
          quantity: item.quantity,
          image: item.image
        })),
        shippingAddress,
        paymentMethod,
        notes: ''
      };

      const response = await HttpInterceptor.post('/api/orders', orderPayload);
      const orders = response.data.data; // Backend returns an array of created orders

      if (paymentMethod !== 'Cash' && paymentSettings.paymentSystemMode === 'gateway') {
        setPlacingOrderData(orders);
        setShowSimulatedGateway(true);
        setLoading(false);
        return;
      }

      // Finalize for Manual or COD
      finalizeOrder(orders);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to place order');
      console.error('Error placing order:', error);
      setLoading(false);
    }
  };

  const finalizeOrder = (orders) => {
    // Clear cart
    localStorage.removeItem('marketplaceCart');
    setCart([]);

    toast.success('Order placed successfully!');
    
    // Redirect logic
    const path = location.pathname.startsWith('/student/') ? '/student/orders' : '/orders';
    
    if (paymentMethod !== 'Cash' && paymentSettings.paymentSystemMode === 'manual') {
      toast.info('Please upload payment receipt to confirm your order.', { autoClose: 6000 });
    }
    
    navigate(path);
  };

  const handleSimulatedPayment = async (status) => {
    setLoading(true);
    try {
      // Simulate calling each order's callback
      for (const order of placingOrderData) {
        await HttpInterceptor.post('/api/payments/callback', {
          orderId: order._id,
          paymentId: 'SIM_' + Math.random().toString(36).substr(2, 9),
          status: status
        });
      }
      
      finalizeOrder(placingOrderData);
    } catch (error) {
      toast.error('Payment callback failed');
    } finally {
      setLoading(false);
      setShowSimulatedGateway(false);
    }
  };

  const handleCancelGateway = async () => {
    if (!placingOrderData || placingOrderData.length === 0) {
      setShowSimulatedGateway(false);
      return;
    }

    setLoading(true);
    try {
      const checkoutId = placingOrderData[0].checkoutId;
      // Tell backend to cancel these orders and hide them from history
      await HttpInterceptor.patch(`/api/orders/group/${checkoutId}/abandon`);
      toast.info('Order was not placed. Items remain in your cart.');
    } catch (error) {
      console.error('Failed to cleanup abandoned order:', error);
    } finally {
      setLoading(false);
      setShowSimulatedGateway(false);
      setPlacingOrderData(null);
    }
  };

  const calculateDiscountedPrice = (price, discount) => {
    return price * (1 - discount / 100);
  };
  // Check if saved address from profile is actually populated with usable data
  const hasSavedAddress = !!(savedAddressFromProfile?.addressLine1 || savedAddressFromProfile?.city);

  const fetchCityStateFromPincode = async (pincode) => {
    if (pincode.length !== 6 || !/^[0-9]{6}$/.test(pincode)) return;
    
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      if (data && data[0] && data[0].Status === 'Success') {
        const { District, State } = data[0].PostOffice[0];
        setShippingAddress(prev => ({
          ...prev,
          city: District,
          state: State,
          pincode: pincode
        }));
      }
    } catch (error) {
      console.error('Error fetching pincode details:', error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">

      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-3xl font-bold mb-6">Checkout</h1>


        {session && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 mb-8 shadow-sm">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-full shadow-md text-3xl">👤</div>
                <div>
                  <h3 className="text-lg font-bold text-purple-900">Account: {session?.user?.name || session?.name}</h3>
                  {hasSavedAddress ? (
                    <div className="mt-1">
                      <p className="text-sm font-medium text-purple-700 flex items-center gap-1">
                        <span className="opacity-70">Saved Address:</span> 
                        {savedAddressFromProfile.city}, {savedAddressFromProfile.state} - {savedAddressFromProfile.pincode}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-purple-600/70 italic mt-1">New customer account.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Shipping Address Form / Login Requirement */}
          <div className="bg-white rounded-xl shadow-md p-6 h-fit">
            {!session && !isSessionLoading ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                  <span className="text-3xl text-purple-600">📱</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Quick Checkout</h2>
                <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
                  Enter your mobile number to receive a one-time password and complete your purchase securely.
                </p>
                
                <div className="space-y-4 max-w-sm mx-auto">
                  {!isOtpSent ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">+91</span>
                        <input
                          type="tel"
                          placeholder="Mobile Number"
                          value={mobile}
                          onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          className="w-full pl-12 pr-4 py-3 border-2 border-gray-100 rounded-xl focus:border-purple-500 focus:ring-0 transition-all text-lg tracking-wider"
                        />
                      </div>
                      <button
                        onClick={handleSendOtp}
                        disabled={isSendingOtp || mobile.length < 10}
                        className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isSendingOtp ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : 'Send OTP'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2">
                       <input
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        autoFocus
                        maxLength={6}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-4 py-3 border-2 border-purple-100 rounded-xl focus:border-purple-500 focus:ring-0 transition-all text-center text-2xl tracking-[0.5em] font-bold text-purple-600"
                      />
                      <button
                        onClick={handleVerifyOtp}
                        disabled={isVerifyingOtp || otp.length < 6}
                        className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                         {isVerifyingOtp ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : 'Verify & Continue'}
                      </button>
                      <button 
                        onClick={() => setIsOtpSent(false)}
                        className="text-xs text-purple-600 font-semibold hover:underline"
                      >
                        Change mobile number
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-6 px-4">
                  <p className="text-[10px] text-gray-400 leading-relaxed">
                    By clicking "Send OTP" or "Verify", you implicitly agree to our <Link to="/terms-of-service" className="text-purple-400 underline">Terms of Service</Link> and <Link to="/privacy-policy" className="text-purple-400 underline">Privacy Policy</Link>.
                  </p>
                </div>
                
                <div className="mt-8 pt-6 border-t border-gray-50 grid grid-cols-3 gap-2">
                  <div className="flex flex-col items-center">
                    <span className="text-lg">📦</span>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter mt-1">Order Tracking</p>
                  </div>
                  <div className="flex flex-col items-center border-x border-gray-50 px-2">
                    <span className="text-lg">🛡️</span>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter mt-1">Buyer Protection</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-lg">💳</span>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter mt-1">Secure Payments</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-4">Shipping Address</h2>

                {session && hasSavedAddress && (
                  <div className="mb-6 bg-purple-50 p-4 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="radio"
                        id="useSaved"
                        name="addressType"
                        checked={useSavedAddress}
                        onChange={() => setUseSavedAddress(true)}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <label htmlFor="useSaved" className="font-medium text-gray-800 cursor-pointer">
                        Use saved address
                      </label>
                    </div>
                    {useSavedAddress && (
                      <div className="ml-7 mt-2 p-3 bg-white rounded border border-purple-200 text-sm text-gray-700 shadow-sm">
                        <p className="font-semibold">{savedAddressFromProfile.name}</p>
                        <p>{savedAddressFromProfile.phone}</p>
                        {savedAddressFromProfile.addressLine1 && savedAddressFromProfile.city && savedAddressFromProfile.state && savedAddressFromProfile.pincode ? (
                          <>
                            <p>{savedAddressFromProfile.addressLine1}</p>
                            {savedAddressFromProfile.addressLine2 && <p>{savedAddressFromProfile.addressLine2}</p>}
                            <p>{savedAddressFromProfile.city}, {savedAddressFromProfile.state} - {savedAddressFromProfile.pincode}</p>
                            <p>{savedAddressFromProfile.country || 'India'}</p>
                          </>
                        ) : (
                          <div className="mt-3 text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
                            <p className="font-semibold flex items-center gap-2 mb-1">
                              <span className="text-xl">⚠️</span> Address Incomplete
                            </p>
                            <p className="text-xs mb-3 text-amber-800">
                              Your saved address is missing some details. Please update it to proceed.
                            </p>
                            <button
                              type="button"
                              onClick={() => navigate('/student/profile', { state: { from: location.pathname } })}
                              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-bold transition-colors shadow-sm"
                            >
                              Update Address Details
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-3 mt-3">
                      <input
                        type="radio"
                        id="useNew"
                        name="addressType"
                        checked={!useSavedAddress}
                        onChange={() => setUseSavedAddress(false)}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <label htmlFor="useNew" className="font-medium text-gray-800 cursor-pointer">
                        Use a different address
                      </label>
                    </div>
                  </div>
                )}

                <form onSubmit={handlePlaceOrder}>
                  <div className="space-y-4">
                    {(!useSavedAddress || !savedAddressFromProfile) && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.name}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            value={shippingAddress.phone}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 1 <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.addressLine1}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, addressLine1: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 2
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.addressLine2}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, addressLine2: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pincode <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.pincode}
                            onChange={(e) => {
                              const val = e.target.value;
                              setShippingAddress({ ...shippingAddress, pincode: val });
                              if (val.length === 6) fetchCityStateFromPincode(val);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="6-digit Pincode"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              City <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={shippingAddress.city}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              State <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={shippingAddress.state}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        <option value="UPI">UPI</option>
                        <option value="Credit Card">Credit Card</option>
                        <option value="Debit Card">Debit Card</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Cash">Cash on Delivery</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={loading || cart.length === 0}
                      className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50"
                    >
                      {loading ? 'Placing Order...' : 'Place Order'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Simulated Payment Gateway Modal */}
          {showSimulatedGateway && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-300">
                <div className="bg-purple-600 p-6 text-white text-center">
                  <div className="text-4xl mb-2">💳</div>
                  <h3 className="text-xl font-bold uppercase tracking-wider">Yogvital Secure Gateway</h3>
                  <p className="text-purple-100 text-sm mt-1">Simulating Real Payment Process</p>
                </div>
                
                <div className="p-8">
                  <div className="text-center mb-8">
                    <p className="text-gray-500 text-sm mb-1 uppercase font-bold tracking-widest">Amount to Pay</p>
                    <p className="text-4xl font-extrabold text-gray-900 italic">₹{Math.round(calculateTotal()).toLocaleString()}</p>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={() => handleSimulatedPayment('success')}
                      disabled={loading}
                      className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>✅ Simulate Successful Payment</>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleSimulatedPayment('failed')}
                      disabled={loading}
                      className="w-full py-4 bg-red-50 hover:bg-red-100 text-red-600 border-2 border-red-100 rounded-xl font-bold transition-all"
                    >
                       ❌ Simulate Payment Failure
                    </button>
                    
                    <button
                      onClick={handleCancelGateway}
                      disabled={loading}
                      className="w-full py-2 text-gray-400 hover:text-gray-600 font-medium text-sm transition-colors"
                    >
                      Cancel and Return to Checkout
                    </button>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 border-t border-gray-100 text-center">
                  <p className="text-[10px] text-gray-400 font-mono uppercase tracking-tighter">
                    Transaction Encrypted (AES-256) | Gateway ID: YV-GW-001
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
            <div className="space-y-4 mb-6">
              {cart.map(item => (
                <div key={item.productId} className="flex items-center gap-4 border-b pb-4">
                  <img
                    src={item.image || '/placeholder.png'}
                    alt={item.title}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold">{item.title}</h4>
                    <p className="text-gray-600 text-sm">
                      Qty: {item.quantity} × ₹{calculateDiscountedPrice(item.price, item.discount).toFixed(2)}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      Includes {item.gstRate || 18}% GST
                      {item.hsnCode && ` • HSN: ${item.hsnCode}`}
                    </p>
                  </div>
                  <div className="font-bold">
                    ₹{(calculateDiscountedPrice(item.price, item.discount) * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-2">
              {(() => {
                const exactTotal = calculateTotal();
                const roundedTotal = Math.round(exactTotal);
                const roundOffAmount = roundedTotal - exactTotal;

                return (
                  <>
                    {(() => {
                      let baseTotal = 0;
                      const gstBreakdown = {};
                      const buyerState = shippingAddress?.state?.toLowerCase()?.trim() || '';

                      cart.forEach(item => {
                        const itemTotalFixed = calculateDiscountedPrice(item.price, item.discount) * item.quantity;
                        const gstRate = item.gstRate || 18;
                        const gstRateMultiplier = gstRate / 100;
                        const itemBase = itemTotalFixed / (1 + gstRateMultiplier);
                        baseTotal += itemBase;
                        const itemGst = itemTotalFixed - itemBase;

                        const sellerState = item.resellerState?.toLowerCase()?.trim() || 'platform';
                        const isInterState = buyerState && sellerState !== 'platform' && buyerState !== sellerState;

                        if (!gstBreakdown[gstRate]) {
                          gstBreakdown[gstRate] = { totalGst: 0, cgst: 0, sgst: 0, igst: 0 };
                        }

                        if (isInterState) {
                          gstBreakdown[gstRate].igst += itemGst;
                          gstBreakdown[gstRate].totalGst += itemGst;
                        } else {
                          gstBreakdown[gstRate].cgst += itemGst / 2;
                          gstBreakdown[gstRate].sgst += itemGst / 2;
                          gstBreakdown[gstRate].totalGst += itemGst;
                        }
                      });

                      return (
                        <>
                          <div className="flex justify-between text-gray-600">
                            <span>Subtotal (Excl. Tax):</span>
                            <span>₹{baseTotal.toFixed(2)}</span>
                          </div>
                          {Object.keys(gstBreakdown).sort((a, b) => a - b).map(rate => {
                            const rateGst = gstBreakdown[rate];
                            return (
                              <React.Fragment key={rate}>
                                {rateGst.igst > 0 && (
                                  <div className="flex justify-between text-gray-600 text-sm">
                                    <span>IGST ({rate}%):</span>
                                    <span>₹{rateGst.igst.toFixed(2)}</span>
                                  </div>
                                )}
                                {(rateGst.cgst > 0 || rateGst.sgst > 0) && (
                                  <>
                                    <div className="flex justify-between text-gray-600 text-sm">
                                      <span>CGST ({rate / 2}%):</span>
                                      <span>₹{rateGst.cgst.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600 text-sm">
                                      <span>SGST ({rate / 2}%):</span>
                                      <span>₹{rateGst.sgst.toFixed(2)}</span>
                                    </div>
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </>
                      );
                    })()}
                    {roundOffAmount !== 0 && (
                      <div className="flex justify-between text-gray-500 text-sm">
                        <span>Round off:</span>
                        <span>{roundOffAmount > 0 ? '+' : ''}₹{roundOffAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold pt-2 border-t border-gray-100 mt-2">
                      <span>Total:</span>
                      <span>₹{roundedTotal.toFixed(2)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {session && (
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-400">
              Switching accounts? <span 
                onClick={() => Logout(navigate, setSession)} 
                className="text-purple-600 font-bold hover:underline cursor-pointer"
              >
                Logout
              </span> here and verify your other mobile number.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Checkout;



